import React, { useEffect, useState } from 'react';
import './ApiKeysPage.scss';
import Loader from '../../../shared/components/common/loader/Loader';
import { getApiKeys, deleteApiKey } from './services/ApiKeysService';
import { ApiKeyResponse } from './models/ApiKeyResponse';
import { useAccount } from '../../../shared/contexts/account/AccountContexts';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { Button } from 'primereact/button';
import { confirmDialog, ConfirmDialog } from 'primereact/confirmdialog';
import { Dialog } from 'primereact/dialog';
import { useTranslation } from 'react-i18next';
import { format, isAfter } from 'date-fns';
import EmptyState from '../../../shared/components/common/empty-state/EmptyState';
import MoreBtn from '../../../shared/components/common/more-btn/MoreBtn';
import ApiKeyCreate from './components/create/ApiKeyCreate';
import ApiKeyEdit from './components/edit/ApiKeyEdit';
import ApiKeyExtend from './components/extend/ApiKeyExtend';
import { PROJECTS } from '../../../shared/routes/path';
import { useNavigate } from 'react-router-dom';
import { isAdminOrOwner } from '../../../shared/utils/utils';

const ApiKeysPage: React.FC = () => {
  const { t } = useTranslation();
  const { accountResponse } = useAccount();
  const [apiKeys, setApiKeys] = useState<ApiKeyResponse[] | null>(null);
  const [showCopiedNotification, setShowCopiedNotification] = useState(false);
  const [dialogMode, setDialogMode] = useState<{ type: 'create' | 'edit' | 'extend'; apiKey?: ApiKeyResponse } | null>(null);

  const navigate = useNavigate();

  const hideDialog = () => {
    setDialogMode(null);
    loadApiKeys();
  };

  useEffect(() => {
    loadApiKeys();
  }, []);

  useEffect(() => {
    if (showCopiedNotification) {
      const timer = setTimeout(() => {
        setShowCopiedNotification(false);
      }, 2000);
      return () => clearTimeout(timer);
    }
  }, [showCopiedNotification]);

  const loadApiKeys = async () => {
    try {
      const data = await getApiKeys();
      setApiKeys(data);
    } catch (error) {
      console.error('Failed to load API keys:', error);
    }
  };

  const getKeyStatus = (expires: Date | null | undefined) => {
    if (!expires) {
      return { status: 'active', label: t('neverExpires') };
    }

    const now = new Date();
    const expiresDate = new Date(expires);
    const daysUntilExpiry = Math.ceil((expiresDate.getTime() - now.getTime()) / (1000 * 60 * 60 * 24));

    if (isAfter(now, expiresDate)) {
      return { status: 'expired', label: t('expired') };
    } else if (daysUntilExpiry <= 7) {
      return { status: 'expires-soon', label: t('expiresInDays', { days: daysUntilExpiry }) };
    } else {
      return { status: 'active', label: t('expiresInDays', { days: daysUntilExpiry }) };
    }
  };

  const confirmDelete = (apiKey: ApiKeyResponse) => {
    confirmDialog({
      message: t('areYouSureDeleteApiKey'),
      header: t('areYouSure'),
      defaultFocus: 'accept',
      accept: () => handleDelete(apiKey.key),
    });
  };

  const handleDelete = async (key: string) => {
    try {
      const result = await deleteApiKey(key);
      if (result.success) {
        await loadApiKeys();
      }
    } catch (error) {
      console.error('Failed to delete API key:', error);
    }
  };

  const addNewApiKey = () => setDialogMode({ type: 'create' });

  const editApiKey = (apiKey: ApiKeyResponse) => setDialogMode({ type: 'edit', apiKey });

  const extendApiKey = (apiKey: ApiKeyResponse) => setDialogMode({ type: 'extend', apiKey });

  const getCreateApiKeyButton = () => {
    return (
      <Button
        label={t('createApiKey')}
        className="btn btn--sm"
        onClick={() => addNewApiKey()}
      />
    );
  };

  const canManage = accountResponse != null && isAdminOrOwner(accountResponse.role);

  const emptyStateData = {
    title: canManage ? t('emptyStateApiKeys') : t('emptyStateApiKeysReadonly'),
    imageClassName: 'img--project',
    children: canManage ? getCreateApiKeyButton() : undefined,
  };

  if (!accountResponse || !accountResponse.addons.includes('API_KEYS')) {
    navigate(PROJECTS);
    return <></>;
  }

  const actionBodyTemplate = (apiKey: ApiKeyResponse) => {
    return (
      <div className="buttons-list">
        <Button 
          className="action-btn" 
          onClick={() => editApiKey(apiKey)}
          title={t('edit')}
        >
          <span className="ico--edit ico--xs" />
        </Button>
        <Button 
          className="action-btn" 
          onClick={() => extendApiKey(apiKey)}
          title={t('extendValidity')}
        >
          <span className="ico--calendar ico--xs" />
        </Button>
        <Button 
          className="action-btn" 
          onClick={() => confirmDelete(apiKey)}
          title={t('delete')}
        >
          <span className="ico--trash ico--xs" />
        </Button>
      </div>
    );
  };

  const statusBodyTemplate = (apiKey: ApiKeyResponse) => {
    const { status, label } = getKeyStatus(apiKey.expires);
    return (
      <span className={`status-${status}`}>
        {label}
      </span>
    );
  };

  const copyToClipboard = async (text: string) => {
    try {
      await navigator.clipboard.writeText(text);
      setShowCopiedNotification(true);
    } catch (err) {
      console.error('Failed to copy text: ', err);
    }
  };

  const keyBodyTemplate = (apiKey: ApiKeyResponse) => {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
        <span style={{ fontFamily: 'monospace', fontSize: '12px' }}>
          {apiKey.key.substring(0, 20)}...
        </span>
        <Button 
          className="action-btn" 
          onClick={() => copyToClipboard(apiKey.key)}
          title={t('copy')}
        >
          <span className="ico--copy ico--xs" />
        </Button>
      </div>
    );
  };

  if (!apiKeys || !accountResponse) {
    return (
      <div className="api-keys-page">
        <Loader />
      </div>
    );
  }

  return (
    <div className="api-keys-page">
      <ConfirmDialog className="dialog-box" />
      <Dialog
        visible={dialogMode !== null}
        onHide={() => setDialogMode(null)}
        header={dialogMode?.type === 'create' ? t('createApiKey') : dialogMode?.type === 'edit' ? t('editApiKey') : t('extendValidity')}
        className="dialog-box"
        modal
      >
        {dialogMode?.type === 'create' && <ApiKeyCreate afterSave={hideDialog} />}
        {dialogMode?.type === 'edit' && dialogMode.apiKey && <ApiKeyEdit apiKey={dialogMode.apiKey} afterSave={hideDialog} />}
        {dialogMode?.type === 'extend' && dialogMode.apiKey && <ApiKeyExtend apiKey={dialogMode.apiKey} afterSave={hideDialog} />}
      </Dialog>

      {apiKeys && apiKeys.length > 0 ? (
        <>
          {canManage && (
            <div className="api-keys-page__header">
              <div className="api-keys-page__add">
                <Button
                  label={t('createApiKey')}
                  className="btn btn--sm"
                  onClick={() => addNewApiKey()}
                />
              </div>
            </div>
          )}

          <div className="api-keys-page__table">
            <DataTable
              stripedRows
              paginator
              paginatorClassName="pagination"
              removableSort
              sortField="created"
              sortOrder={-1}
              rows={50}
              rowsPerPageOptions={[10, 25, 50]}
              value={apiKeys}
              tableStyle={{ minWidth: '50rem' }}
              tableClassName="table"
              className="table-wrapper"
              currentPageReportTemplate={t('rowsNumber') + ':'}
              paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
            >
              <Column
                field="key"
                header={t('apiKey')}
                body={keyBodyTemplate}
                headerClassName="table__header"
                style={{ minWidth: '200px' }}
              />
              <Column
                field="description"
                header={t('description')}
                body={(apiKey) => apiKey.description || '-'}
                headerClassName="table__header"
                style={{ minWidth: '150px' }}
              />
              <Column
                field="status"
                header={t('status')}
                body={(apiKey) => (
                  <span className={`status-${apiKey.status === 'ACTIVE' ? 'active' : 'inactive'}`}>
                    {apiKey.status === 'ACTIVE' ? t('active') : t('inactive')}
                  </span>
                )}
                headerClassName="table__header"
                style={{ minWidth: '100px' }}
              />
              <Column
                field="created"
                header={t('created')}
                sortable
                body={(apiKey) => format(new Date(apiKey.created), 'MMM d, yyyy')}
                headerClassName="table__header"
                style={{ minWidth: '120px' }}
              />
              <Column
                field="expires"
                header={t('validity')}
                sortable
                body={statusBodyTemplate}
                headerClassName="table__header"
                style={{ minWidth: '150px' }}
              />
              {canManage && (
                <Column
                  field="creatorEmail"
                  header={t('createdBy')}
                  body={(apiKey) => apiKey.creatorEmail || '-'}
                  headerClassName="table__header"
                  style={{ minWidth: '150px' }}
                />
              )}
              {canManage && (
                <Column
                  header={t('action')}
                  body={(apiKey) => (
                    <MoreBtn children={actionBodyTemplate(apiKey)} rowId={apiKey.id} />
                  )}
                  headerClassName="table__header table__header--center"
                  bodyClassName="table__cell table__cell--center"
                  style={{ width: '50px', minWidth: '50px', maxWidth: '50px' }}
                />
              )}
            </DataTable>
          </div>
        </>
      ) : (
        <div className="empty-state-page">
          <EmptyState emptyStateData={emptyStateData} />
        </div>
      )}


      {/* Copy Notification */}
      {showCopiedNotification && (
        <div className="toast">
          {t('copied')}
        </div>
      )}
    </div>
  );
};

export default ApiKeysPage;
